import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const villaTypes = [
  { icon: "fas fa-water", title: "Waterfront developments with scenic views" },
  { icon: "fas fa-people-roof", title: "Family-friendly gated communities" },
  { icon: "fas fa-gem", title: "Premium neighborhoods with luxury amenities" },
  { icon: "fas fa-arrow-trend-up", title: "Emerging areas with high investment potential" },
];

const purchaseSteps = [
  ["Understand Your Needs", "We start by understanding your budget, preferred location, and lifestyle goals."],
  ["Shortlist Properties", "You receive curated options that match your criteria, saving you time and effort."],
  ["Property Viewings", "We arrange viewings at your convenience so you can evaluate each option carefully."],
  ["Negotiation and Purchase", "We guide you through offers, negotiations, and legal steps to secure your villa."],
];

const faqs = [
  {
    icon: "fas fa-globe",
    question: "Can foreigners buy villas in Abu Dhabi?",
    answer: "Yes. Foreigners can buy villas in designated areas. They get full ownership or long-term leasehold options, depending on the project.",
  },
  {
    icon: "fas fa-handshake",
    question: "How do I choose the right villa in Abu Dhabi?",
    answer: "You just need to explain your requirements, preferences, and budget. We will filter all available villas in Abu Dhabi and then recommend the best villas to you. Visit them, think over them, and then make the right choice.",
  },
  {
    icon: "fas fa-chart-line",
    question: "Are villas in Abu Dhabi a good investment?",
    answer: "Yes. Of course. Buying a villa in Abu Dhabi is always a smart choice due to growing demand, spacious living, and long-term value appreciation.",
  },
  {
    icon: "fas fa-coins",
    question: "What additional costs should I consider when buying a villa?",
    answer: "You should consider transfer fees, agency commission, and possible maintenance costs. These vary depending on the property and transaction.",
  },
  {
    icon: "far fa-clock",
    question: "How long does it take to buy a villa in Abu Dhabi?",
    answer: "The process can take a few weeks, depending on financing, documentation, and negotiations.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Villas to Buy in Abu Dhabi", item: "https://nvrealestate.ae/villas-to-buy-abu-dhabi" },
  ],
};

function VillasToBuyAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Villas to Buy in Abu Dhabi – Luxury Villas UAE</title>
        <meta
          name="description"
          content="NV Real Estate team has access to almost all top villas in Abu Dhabi. We can recommend the best villas to buy in Abu Dhabi based on your words. Let us connect now."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/villas-to-buy-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi villa specialists</span>
              <h1 id="villa-page-title">Villas to Buy in Abu Dhabi – Find Your Perfect Home</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=Villa&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore villas
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/villas-to-buy-abu-dhabi/02.png"
                alt="Villas to buy in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Ready-to-move & off-plan</strong>
                <span>Access to top villa communities</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Villas to Buy in Abu Dhabi – Find Your Perfect Home</h2>
              <p>
                Nova Vision Real Estate is committed to helping villa seekers to find the best
                villas to buy in Abu Dhabi. We have access to all the top ready-to-move and
                under-construction villas. We help people buy the best homes for their families,
                considering their requirements, expectations, and budget.
              </p>
              <p>
                You have visited us means you are also looking for a villa to buy in Abu Dhabi. We
                appreciate your decision as you have chosen the most attractive real estate markets
                in the region, known for offering a great lifestyle that fits your needs today and
                your plans for the future. Abu Dhabi is known for offering modern living, privacy,
                and long-term value. Villas here are known for offering comfort, space, and quality
                living. Rest assured, our NV Real Estate team will help you filter the best options
                matching your words and help you make the best choice for you.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="villa-choice-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why a villa</span>
              <h2 id="villa-choice-heading">What Makes Villas in Abu Dhabi a Great Residential Choice?</h2>
              <p>
                Villas in Abu Dhabi are known for their spacious layouts, premium construction, and
                well-planned communities. They offer you flexibility that apartments often cannot.
                In Abu Dhabi, you find so many popular villa communities, and they all are known for
                providing larger living spaces, private outdoor spaces, peaceful surroundings with
                enhanced privacy, and access to facilities like parks, pools, and gyms. Also, they
                offer strong potential for long-term value growth.
              </p>
              <p>
                Buying villas in Abu Dhabi means investing in a great lifestyle and a safe
                environment. You find villas in
              </p>
            </div>
            <div className="villa-benefits__grid">
              {villaTypes.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-process villa-section" aria-labelledby="villa-process-title">
          <div className="container villa-process__layout">
            <div className="villa-process__aside">
              <span className="villa-kicker">Your buying journey</span>
              <h2 id="villa-process-title">Our Process for Your Villa Purchase</h2>
              <p>
                We know it pretty well that buying your villa can be a complex task for you if you
                are navigating the market for the first time. Hence, we make a well-thought-out
                process to help you accomplish your dream.
              </p>
              <Link className="villa-text-link" to="/contact-us">Speak with an advisor <span aria-hidden="true">-&gt;</span></Link>
            </div>
            <div className="villa-process__steps-wrap">
              <ol className="villa-process__steps">
                {purchaseSteps.map(([title, text], index) => (
                  <li key={title}>
                    <span className="villa-process__number">{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{text}</p></div>
                  </li>
                ))}
              </ol>
              <p className="villa-process__closing">
                This approach ensures you make informed decisions without unnecessary stress.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="start-search-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Get started</span>
              <h2 id="start-search-heading">Start Your Villa Search Today!</h2>
              <p>
                Give us a call, share your needs, and we will bring the best villas matching your
                needs.
              </p>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Buyer questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your villa search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`villa-buy-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`villa-buy-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-home" /><div><strong>Curated Villas</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Purchase Support</strong><span>Offer to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email your
              requirements to <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />
    </>
  );
}

export default VillasToBuyAbuDhabi;